import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowRightIcon, ImageXIcon } from '@/components/ui/icons'
import type { Image } from '@/features/photoset/types'
import { shortcutToLabelTuple } from '@/features/photoset/utils'
import { useTranslation } from '@/translations/i18n'

type SpaceCardWrapperProps = HTMLAttributes<HTMLDivElement> & {
	hasImages: boolean
}
export const SpaceCardWrapper = ({
	hasImages,
	className,
	...props
}: SpaceCardWrapperProps) => (
	<div
		className={`flex flex-col gap-3 rounded-[14px] border border-neutral-200 bg-neutral-white p-3 px-4 [&_.show-hover]:hidden [&_.show-not-hover]:flex ${
			hasImages
				? 'hover:[&_.show-hover]:flex hover:[&_.show-not-hover]:hidden'
				: ''
		} ${className ?? ''}`}
		{...props}
	/>
)

type SpaceCardHeaderProps = HTMLAttributes<HTMLDivElement> & {
	headerTitle: string
	images: Image[]
	openSpaceDetailModal?: () => void
	shortcut: string
}
export const SpaceCardHeader = ({
	headerTitle,
	images,
	openSpaceDetailModal,
	shortcut,
	...props
}: SpaceCardHeaderProps) => {
	const { t } = useTranslation()

	return (
		<div
			className='flex w-full justify-between gap-1 [&_h3]:break-words [&_h3]:font-medium [&_h3]:text-base [&_h3]:text-neutral-900 [&_h4]:text-neutral-600 [&_h4]:text-sm'
			{...props}
		>
			<div className='flex flex-col gap-1'>
				<h3>{headerTitle}</h3>
				{images.length >= 1 && (
					<h4>{t('common:x_images', { count: images.length })}</h4>
				)}
			</div>
			{!!openSpaceDetailModal && (
				<ButtonLegacy
					className='show-hover'
					onClick={openSpaceDetailModal}
					shape='square'
					size='sm'
					type='button'
					variant='secondary'
				>
					<ArrowRightIcon />
				</ButtonLegacy>
			)}
			<div
				className={`flex gap-1 ${openSpaceDetailModal ? 'show-not-hover' : ''}`}
			>
				{shortcutToLabelTuple(shortcut).map((shortcutLabel) => (
					<div
						className='flex h-[22px] w-[22px] items-center justify-center rounded border border-neutral-200 bg-neutral-50 text-neutral-500 text-sm'
						key={shortcutLabel}
					>
						{shortcutLabel}
					</div>
				))}
			</div>
		</div>
	)
}

export const SpaceCardImagesPreviewGrid = forwardRef<
	HTMLDivElement,
	HTMLAttributes<HTMLDivElement> & { $isDraggingOver?: boolean }
>(({ $isDraggingOver, className, ...props }, ref) => (
	<div
		className={`grid w-full grid-cols-[repeat(4,1fr)] grid-rows-[1fr_1fr] gap-2 rounded-[10px] transition-all duration-150 ease-in-out ${
			$isDraggingOver
				? 'bg-blue-100 shadow-[0_0_0_4px_var(--color-blue-100)]'
				: ''
		} ${className ?? ''}`}
		ref={ref}
		{...props}
	/>
))

interface SpaceCardImagePreviewSlotProps {
	image: Image
	otherImagesCount: number
	overlayComponent?: ReactNode
}

export const SpaceCardImagePreviewSlot = forwardRef<
	HTMLDivElement,
	SpaceCardImagePreviewSlotProps
>(({ image, otherImagesCount, overlayComponent }, ref) => (
	<div
		className={`relative aspect-square overflow-hidden border border-neutral-50 bg-neutral-100 first:col-[span_2] first:row-[span_2] first:rounded-[10px] [&:not(:first-child)]:rounded-md ${
			otherImagesCount >= 1
				? 'last:relative last:[&_img]:blur-[3px] last:[&_span]:absolute last:[&_span]:inset-0 last:[&_span]:flex last:[&_span]:items-center last:[&_span]:justify-center last:[&_span]:font-medium last:[&_span]:text-neutral-white last:[&_span]:text-sm last:[&_span]:shadow-lg'
				: ''
		} [&_img]:h-full [&_img]:w-full [&_img]:object-cover`}
		ref={ref}
	>
		{overlayComponent && (
			<div className='absolute top-0 left-0 h-full w-full'>
				{overlayComponent}
			</div>
		)}

		{image.thumbnails?.xs ? (
			// biome-ignore lint/correctness/useImageSize: the width and height are dynamic
			<img alt={image.name} key={image.id} src={image.thumbnails.xs} />
		) : (
			<div
				className='flex h-full flex-col items-center justify-center'
				title={image.name}
			>
				<ImageXIcon size={18} />
			</div>
		)}

		{otherImagesCount >= 1 && <span>+{otherImagesCount}</span>}
	</div>
))

export const SpaceCardEmptyPreviewSlot = () => (
	<div className='relative aspect-square overflow-hidden border border-neutral-50 bg-neutral-100 first:col-[span_2] first:row-[span_2] first:rounded-[10px] [&:not(:first-child)]:rounded-md' />
)
